import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import roleTranslationService from '../services/roleTranslationService';

/**
 * Hook for getting role translations from the database
 * Automatically loads all translations for the current user language on mount
 * Provides a function to get translated labels with fallback to key
 */
export const useRoleTranslation = () => {
  const { i18n } = useTranslation();
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);

  // Load translations when language changes
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        setLoading(true);
        const currentLanguage = i18n.language || 'fr';
        // Clear cache for the new language to force reload
        roleTranslationService.clearCache(currentLanguage);
        const translationsData = await roleTranslationService.getTranslations(currentLanguage);
        setTranslations(translationsData);
      } catch (error) {
        console.error('Error loading role translations:', error);
        setTranslations({});
      } finally {
        setLoading(false);
      }
    };

    loadTranslations();
  }, [i18n.language]);

  /**
   * Get translated label for a role key.
   * role-medident-ge remains readable until stored roles are migrated.
   * @param {string} key - Translation key (e.g., "role-dentist")
   * @returns {string} Translated label or key if not found
   */
  const getRoleLabel = useCallback((key) => {
    if (!key) return '';
    const resolved = key === 'role-medident-ge' ? 'role-medident' : key;
    return translations[resolved] || translations[key] || resolved;
  }, [translations]);

  return {
    getRoleLabel,
    translations,
    loading
  };
};

export default useRoleTranslation;

